import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { getOrderPublicForm, submitOrderPublicForm } from '../app/api';
import { useTranslation } from '../app/LanguageContext';

const OrderPublicFormPage = () => {
  const { workspaceId, token } = useParams();
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState(null);
  const [lineItemValues, setLineItemValues] = useState({});

  useEffect(() => {
    const load = async () => {
      if (!workspaceId || !token) {
        setError(translate('public_form.invalid_link') || 'Link inválido');
        setLoading(false);
        return;
      }
      try {
        const res = await getOrderPublicForm(workspaceId, token);
        const data = res?.data || res;
        if (!data) {
          setError(translate('public_form.unavailable') || 'Formulário indisponível');
          return;
        }
        setFormData(data);
        const itemV = {};
        (data.line_items || []).forEach((li) => {
          itemV[li.item_id] = {};
          (li.customization_fields || []).forEach((f) => {
            itemV[li.item_id][f.key] = f.value || '';
          });
        });
        setLineItemValues(itemV);
      } catch (err) {
        setError(err.message || (translate('public_form.unavailable') || 'Formulário indisponível'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [workspaceId, token, translate]);

  const handleLineItemFieldChange = (itemId, key, value) => {
    setLineItemValues((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [key]: value },
    }));
  };

  const handleSubmit = async () => {
    if (!workspaceId || !token || !formData) return;
    setSubmitting(true);
    try {
        const lineItems = (formData.line_items || []).map((li) => ({
        line_id: li.line_id,
        item_id: li.item_id,
        customization_fields: (li.customization_fields || []).map((f) => ({
          key: f.key,
          name: f.name,
          value_type: f.value_type,
          value: (lineItemValues[li.item_id] || {})[f.key] ?? '',
        })),
      }));
      await submitOrderPublicForm(workspaceId, token, {
        order_customization_fields: [],
        line_items: lineItems,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || (translate('public_form.submit_error') || 'Erro ao enviar'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field, value, onChange) => {
    const val = value ?? '';
    if (field.value_type === 'textarea') {
      return (
        <TextField
          fullWidth
          size="small"
          label={field.name}
          value={val}
          onChange={(e) => onChange(e.target.value)}
          multiline
          rows={3}
          sx={{ mt: 1 }}
        />
      );
    }
    if (field.value_type === 'number') {
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          label={field.name}
          value={val}
          onChange={(e) => onChange(e.target.value)}
          sx={{ mt: 1 }}
        />
      );
    }
    if (field.value_type === 'date') {
      return (
        <TextField
          fullWidth
          size="small"
          type="date"
          label={field.name}
          value={val}
          onChange={(e) => onChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 1 }}
        />
      );
    }
    if (field.value_type === 'select' && field.options && field.options.length > 0) {
      const options = [...field.options];
      if (field.allow_other) {
        options.push(translate('public_form.other') || 'Outro');
      }
      return (
        <TextField
          fullWidth
          size="small"
          select
          label={field.name}
          value={val}
          onChange={(e) => onChange(e.target.value)}
          sx={{ mt: 1 }}
        >
          <MenuItem value="">
            <em>{translate('public_form.select') || 'Selecione'}</em>
          </MenuItem>
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    return (
      <TextField
        fullWidth
        size="small"
        label={field.name}
        value={val}
        onChange={(e) => onChange(e.target.value)}
        sx={{ mt: 1 }}
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 4, px: 2 }}>
        <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="success.main" gutterBottom>
            {translate('public_form.success') || 'Formulário enviado com sucesso!'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('public_form.success_message') || 'Obrigado. Suas informações foram recebidas.'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const lineItemsWithFields = (formData?.line_items || []).filter((li) => (li.customization_fields || []).length > 0);
  const hasLineItems = lineItemsWithFields.length > 0;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        py: 4,
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 640, mx: 'auto' }}>
        <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {translate('public_form.title') || 'Formulário de Customizações'}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {formData?.customer_name || ''}
          </Typography>
          {formData?.title && (
            <Typography variant="subtitle1" color="text.secondary">
              {formData.title}
            </Typography>
          )}
        </Paper>

        {hasLineItems &&
          lineItemsWithFields.map((li) => (
            <Paper key={li.item_id} elevation={2} sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {li.block_title || li.product_name || li.item_description || `Item`}
                {li.quantity > 1 && ` (${li.quantity}x)`}
              </Typography>
              {(li.customization_fields || []).map((f) => (
                <Box key={f.key}>
                  {renderField(
                    f,
                    (lineItemValues[li.item_id] || {})[f.key],
                    (v) => handleLineItemFieldChange(li.item_id, f.key, v)
                  )}
                </Box>
              ))}
            </Paper>
          ))}

        {!hasLineItems && (
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {translate('public_form.no_fields') || 'Nenhum campo de customização para preencher.'}
            </Typography>
          </Paper>
        )}

        {hasLineItems && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
            >
              {submitting ? (translate('common.saving') || 'Enviando...') : (translate('public_form.submit') || 'Enviar')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OrderPublicFormPage;
